import clsx from "clsx";
import { motion, Variants } from "motion/react";
import React, { FC } from "react";
import { Link } from "react-router-dom";
import { useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  ISettingsSubItem,
  settingsDrawerOpenStateAtom,
  usePrefixedRouting,
} from "@ryde/shared";

import { SettingItemStyled } from "./settings.styled";

const animationVariants: Variants = {
  initial: {
    height: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      duration: 0.7,
      delayChildren: 0.3,
      staggerChildren: 0.05,
    },
  },
  animate: {
    height: 32,
    opacity: 1,
    transition: {
      type: "spring",
      bounce: 0,
      duration: 0.7,
      delayChildren: 0.3,
      staggerChildren: 0.05,
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      type: "spring",
      bounce: 0,
      duration: 0.7,
      delayChildren: 0.3,
      staggerChildren: 0.05,
    },
  },
};

export const SettingItem: FC<ISettingsSubItem> = ({
  title,
  href,
  customRightImage,
  leftIcon,
  buttonModeClickEventHandler,
  active,
}) => {
  const setSettingsDrawerOpenState = useSetRecoilState(
    settingsDrawerOpenStateAtom,
  );
  const { addPrefix } = usePrefixedRouting();

  const handleLinkClick = (): void => {
    if (!href) {
      return;
    }

    if (buttonModeClickEventHandler) {
      buttonModeClickEventHandler();
    }

    setSettingsDrawerOpenState(false);
  };

  const handleItemAreaClick = (): void => {
    if (href) {
      return;
    }

    if (buttonModeClickEventHandler) {
      buttonModeClickEventHandler();
    }

    setSettingsDrawerOpenState(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleItemAreaClick();
    }
  };

  return (
    <SettingItemStyled
      as={motion.div}
      variants={animationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      onClick={handleItemAreaClick}
      {...(!href && { role: "button", tabIndex: 0, onKeyDown: handleKeyDown })}
    >
      {!!href && (
        <Link
          to={addPrefix(href)}
          onClick={handleLinkClick}
          className={clsx({ active })}
        >
          {!!leftIcon && <span className="item_icon">{leftIcon}</span>}
          <span>{title}</span>
        </Link>
      )}

      {!href && (
        <label className={clsx({ active })}>
          {!!leftIcon && <span className="item_icon">{leftIcon}</span>}
          {title}
        </label>
      )}

      {!customRightImage && (
        <SVG_MUI.ArrowsToDottedLineSVG className="arrow_item rtl_rotate" />
      )}
      {customRightImage && <>{customRightImage}</>}
    </SettingItemStyled>
  );
};
